import React, { useState } from 'react';
import styles from '../app.module.css'

const ReqBodies = ({
  requests = {},
  addRequestBody,
  editRequestBody,
  removeRequestBody,
  schemas = [],
  examples = [],
}) => {
  const [currentReqBody, setCurrentReqBody] = useState(null);
  const [reqBodyData, setReqBodyData] = useState({
    name: '',
    description: '',
    contentType: '',
    schema: '',
    example: '',
  });
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [popupType, setPopupType] = useState(null);

  const showPopupModal = (message) => {
    setPopupMessage(message);
    setShowPopup(true);
  }

  const handleInputChange = (field, value) => {
    setReqBodyData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdd = () => {
    setReqBodyData({ name: '', description: '', contentType: '', schema: '', example: '' });
    setCurrentReqBody(null);
    setPopupVisible(true);
  };

  const handleEdit = (name) => {
    const reqBody = requests[name] || {};
    setReqBodyData({
      name,
      description: reqBody.description || '',
      contentType: Object.keys(reqBody.content || {})[0] || '',
      schema: reqBody.content?.[Object.keys(reqBody.content || {})[0]]?.schema?.$ref || '',
      example: reqBody.content?.[Object.keys(reqBody.content || {})[0]]?.examples
        ? Object.keys(reqBody.content[Object.keys(reqBody.content)[0]].examples)[0]
        : '',
    });
    setCurrentReqBody(name);
    setPopupVisible(true);
  };

  const handleSave = () => {
    const { name, description, contentType, schema, example } = reqBodyData;

    if (!currentReqBody && requests[name]) {
      showPopupModal(`A request body with the name "${name}" already exists. Please use a different name.`);
      return;
    }

    if (!name || !contentType || !schema) {
      showPopupModal('All fields are required except description and examples.');
      return;
    }

    const content = {
      [contentType]: {
        schema: {
          $ref: schema,
        },
      },
    };

    if (example) {
      content[contentType].examples = {
        [example]: {
          $ref: `#/components/examples/${example}`,
        },
      };
    }

    const reqBodyToSave = {
      description,
      content,
    };

    if (currentReqBody) {
      editRequestBody(currentReqBody, reqBodyToSave);
    } else {
      addRequestBody(name, reqBodyToSave);
    }

    handleClosePopup();
  };

  const handleDelete = (name) => {
    setDeleteCandidate(name);
    setPopupMessage(`Are you sure you want to delete the Request Body "${name}"?`);
    setPopupType('confirm-delete');
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setPopupVisible(false);
    setReqBodyData({ name: '', description: '', contentType: '', schema: '', example: '' });
    setCurrentReqBody(null);
  };

  return (
    <div className="request-container">

      <h3 className={styles.centeredHeading}>Request Bodies</h3>

      {Object.keys(requests).length > 0 && (
        <div>
          {Object.entries(requests).map(([name, reqBody]) => (
            <div
              key={name}
              className={styles.mainContainer}
            >
              <p>
                <strong>Name:</strong> {name}
              </p>
              <p>
                <strong>Description:</strong> {reqBody.description}
              </p>
              <p>
                <strong>Content Type:</strong> {Object.keys(reqBody.content || {})[0]}
              </p>
              <p>
                <strong>Schema:</strong>{' '}
                {reqBody.content &&
                  Object.values(reqBody.content).length &&
                  reqBody.content[Object.keys(reqBody.content)[0]].schema
                  ? reqBody.content[Object.keys(reqBody.content)[0]].schema.$ref.replace(
                    '#/components/schemas/',
                    ''
                  )
                  : 'None'}
              </p>
              <p>
                <strong>Example:</strong>{' '}
                {reqBody.content?.[Object.keys(reqBody.content || {})[0]]?.examples
                  ? Object.keys(reqBody.content[Object.keys(reqBody.content || {})[0]].examples)[0]
                  : 'None'}
              </p>
              <div className={styles.actionButtonsContainer}>
                <button
                  onClick={() => handleEdit(name)}
                  className={styles.editButton}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(name)}
                  className={styles.deleteButton}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button onClick={handleAdd} className={styles.addNewButton}>+ Add Request Body </button>

      {popupVisible && (
        <div className={styles.popupContainer}>
          <h3>{currentReqBody ? 'Edit Request Body' : 'Add Request Body'}</h3>
          <div className={styles.style3}>
            <label className={styles.labelStyle}>Name:<span className={styles.requiredField}>*</span></label>
            <input
              type="text"
              value={reqBodyData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={styles.textBox}
              disabled={!!currentReqBody}
            />
          </div>
          <div className={styles.style3}>
            <label className={styles.labelStyle}>Description:</label>
            <textarea
              value={reqBodyData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className={styles.textBox}
            />
          </div>
          <div className={styles.style3}>
            <label className={styles.labelStyle}>Content Type:<span className={styles.requiredField}>*</span></label>
            <select
              value={reqBodyData.contentType}
              onChange={(e) => handleInputChange('contentType', e.target.value)}
              className={styles.dropdown}
            >
              <option value="">Select Content Type</option>
              <option value="application/json">application/json</option>
              <option value="application/xml">application/xml</option>
            </select>
          </div>
          <div className={styles.style3}>
            <label className={styles.labelStyle}>Schema:<span className={styles.requiredField}>*</span></label>
            <select
              value={reqBodyData.schema}
              onChange={(e) => handleInputChange('schema', e.target.value)}
              className={styles.dropdown}
            >
              <option value="">Select Schema</option>
              {schemas?.map((schema) => (
                <option key={schema} value={`#/components/schemas/${schema}`}>
                  {schema}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.style3}>
            <label className={styles.labelStyle}>Example:</label>
            <select
              value={reqBodyData.example}
              onChange={(e) => handleInputChange('example', e.target.value)}
              className={styles.dropdown}
            >
              <option value="">Select Example</option>
              {examples?.map((example) => (
                <option key={example} value={example}>
                  {example}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.sNcButtonContainer}>
            <button
              onClick={handleSave}
              className={styles.saveButton}
            >
              Save
            </button>
            <button
              onClick={handleClosePopup}
              className={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {popupVisible && (
        <div
          className={styles.overlay}
          onClick={handleClosePopup}
        ></div>
      )}
      {showPopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.popup}>
            {popupType === 'confirm-delete' && (
              <>
                <h3>Confirm Delete</h3>
                <p>{popupMessage}</p>
                <div className={styles.sNcButtonContainer}>
                  <button
                    onClick={() => {
                      if (deleteCandidate) removeRequestBody(deleteCandidate);
                      setDeleteCandidate(null);
                      setPopupType(null);
                      setShowPopup(false);
                    }}
                    className={styles.deleteButton}
                  >
                    OK
                  </button>
                  <button
                    onClick={() => {
                      setDeleteCandidate(null);
                      setPopupType(null);
                      setShowPopup(false);
                    }}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
            {popupType === 'error' && (
              <>
                <h3>Error</h3>
                <p>{popupMessage}</p>
                <button onClick={() => setShowPopup(false)} className="popup-close-button">
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReqBodies;
